import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type IBankAccount } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface ImportResult {
  total: number;
  imported: number;
  duplicates: number;
  matched: number;
  /** Transactions that landed in a different account than their activity routes
   *  to. Includes ones held back by the amount check, so this is not a subset
   *  of `matched`. */
  mismatched: number;
  errors: string[];
}

const PaymentImportPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Which account this statement belongs to. Bank exports name only the
  // counterparty, never the organization's own account, so it cannot be read
  // from the file — the admin has to say.
  const [accounts, setAccounts] = useState<IBankAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [bankAccountId, setBankAccountId] = useState<number | ''>('');

  // Permission check
  const canImport = hasPermission('payments:write');

  useEffect(() => {
    if (!canImport) {
      setAccountsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.organization.bankAccounts.list();
        if (cancelled) return;
        setAccounts(data);
        const preselected = data.find((a) => a.isDefault) ?? data[0];
        if (preselected) setBankAccountId(preselected._id);
      } catch {
        // Non-fatal: the upload falls back to the org default server-side.
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canImport]);

  const accountLabel = (account: IBankAccount) =>
    account.name ? `${account.name} — ${account.accountNumber}` : account.accountNumber;

  // A single-account organization has nothing to decide.
  const showAccountPicker = useMemo(() => accounts.length > 1, [accounts]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !canImport) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.payments.import(
        file,
        bankAccountId === '' ? undefined : bankAccountId,
      );
      setResult(response.data as ImportResult);
      setFile(null);
      // Reset input
      const fileInput = document.getElementById('bank-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  if (!canImport) {
    return (
      <div className="admin-list-page">
        <h2>{t('payments.importTitle')}</h2>
        <p className="error-message">{t('common.noPermission')}</p>
      </div>
    );
  }

  return (
    <div className="admin-list-page">
      <h2>{t('payments.importTitle')}</h2>

      <div className="form-container" style={{ maxWidth: '600px', margin: '2rem 0' }}>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
          {t('payments.importDesc')}
          <br />
          <small>{t('payments.supportedFormats')}</small>
        </p>

        <form onSubmit={handleUpload}>
          {showAccountPicker && (
            <FormField
              htmlFor="bank-account"
              label={t('payments.destinationAccount')}
              helperText={t('payments.destinationAccountHint')}
              required
            >
              <select
                id="bank-account"
                value={bankAccountId === '' ? '' : String(bankAccountId)}
                onChange={(e) => setBankAccountId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                {accounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {accountLabel(account)}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          <div className="form-group">
            <label htmlFor="bank-file">{t('payments.selectFile')}</label>
            <input
              type="file"
              id="bank-file"
              onChange={handleFileChange}
              accept=".csv"
              required
            />
          </div>

          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={!file || accountsLoading}
          >
            {loading ? t('common.loading') : t('payments.uploadButton')}
          </Button>
        </form>

        {error && (
          <div className="error-message" style={{ marginTop: '1.5rem' }}>
            {error}
          </div>
        )}

        {result && (
          <div className="success-message" style={{ marginTop: '1.5rem', textAlign: 'left' }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>{t('payments.importComplete')}</h4>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              <li>{t('payments.totalFound')}: <strong>{result.total}</strong></li>
              <li>{t('payments.newImported')}: <strong>{result.imported}</strong></li>
              <li>{t('payments.duplicatesSkipped')}: <strong>{result.duplicates}</strong></li>
              <li><strong>{t('payments.autoMatched')}: {result.matched}</strong></li>
            </ul>
            {result.mismatched > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <small style={{ color: 'var(--warning, #ffc107)' }}>
                  {t('payments.accountMismatchWarning', { count: result.mismatched })}
                </small>
              </div>
            )}
            {result.errors.length > 0 && (
              <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '0.5rem' }}>
                <small style={{ color: 'var(--danger)' }}>
                  {result.errors.length} {t('payments.errorsOccurred')}.
                </small>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentImportPage;
